import React, { useEffect, useRef, useState } from 'react';

const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const DEFAULT_MAX_FILES = 10;
const DEFAULT_MAX_FILE_BYTES = 15 * 1024 * 1024;
const EMPTY_FILES: File[] = [];

interface StagedImage {
  id: string;
  file: File;
  previewUrl: string;
}

interface Props {
  isOpen: boolean;
  initialFiles?: File[];
  maxFiles?: number;
  maxFileBytes?: number;
  onClose: () => void;
  onConfirm: (files: File[]) => void;
}

function imageId(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

const ImageUploadPanel: React.FC<Props> = ({
  isOpen,
  initialFiles = EMPTY_FILES,
  maxFiles = DEFAULT_MAX_FILES,
  maxFileBytes = DEFAULT_MAX_FILE_BYTES,
  onClose,
  onConfirm,
}) => {
  const [images, setImages] = useState<StagedImage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const imagesRef = useRef<StagedImage[]>([]);

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(() => () => {
    for (const image of imagesRef.current) URL.revokeObjectURL(image.previewUrl);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    for (const image of imagesRef.current) URL.revokeObjectURL(image.previewUrl);
    const next = initialFiles.map(file => ({ id: imageId(file), file, previewUrl: URL.createObjectURL(file) }));
    imagesRef.current = next;
    setImages(next);
  }, [initialFiles, isOpen]);

  if (!isOpen) return null;

  const addFiles = (incoming: File[]) => {
    setError(null);
    const invalid = incoming.find(file => !ACCEPTED_TYPES.has(file.type));
    if (invalid) {
      setError(`${invalid.name} is not a supported JPG, PNG, or WebP image.`);
      return;
    }
    const oversized = incoming.find(file => file.size > maxFileBytes);
    if (oversized) {
      setError(`${oversized.name} is larger than ${(maxFileBytes / 1024 / 1024).toFixed(0)} MB.`);
      return;
    }

    const current = imagesRef.current;
    const existing = new Set(current.map(image => image.id));
    const additions = incoming
      .filter(file => !existing.has(imageId(file)))
      .map(file => ({ id: imageId(file), file, previewUrl: URL.createObjectURL(file) }));
    if (additions.length === 0) {
      setError('Those images are already staged.');
      return;
    }

    const remaining = Math.max(maxFiles - current.length, 0);
    if (additions.length > remaining) {
      for (const image of additions.slice(remaining)) URL.revokeObjectURL(image.previewUrl);
      setError(`You can stage up to ${maxFiles} images at a time.`);
    }
    const next = [...current, ...additions.slice(0, remaining)];
    imagesRef.current = next;
    setImages(next);
  };

  const removeImage = (id: string) => {
    const current = imagesRef.current;
    const removed = current.find(image => image.id === id);
    if (removed) URL.revokeObjectURL(removed.previewUrl);
    const next = current.filter(image => image.id !== id);
    imagesRef.current = next;
    setImages(next);
  };

  return (
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 p-4"
      onPointerDown={event => event.stopPropagation()}
    >
      <div className="w-full max-w-2xl rounded-xl border border-white/15 bg-[#0E0D0C] p-5 text-white shadow-2xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold">Upload images</h2>
            <p className="mt-1 text-xs text-white/55">Select one image or a set of up to {maxFiles}. Nothing is sent until another capability uses them.</p>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white" aria-label="Close image upload">✕</button>
        </div>

        <button
          type="button"
          aria-label="Choose or drop images"
          onClick={() => inputRef.current?.click()}
          onDragOver={event => event.preventDefault()}
          onDrop={event => {
            event.preventDefault();
            addFiles(Array.from(event.dataTransfer.files));
          }}
          className="mb-4 flex min-h-28 w-full flex-col items-center justify-center rounded-lg border border-dashed border-white/25 bg-white/[0.03] px-4 text-center hover:bg-white/[0.06]"
        >
          <span className="text-2xl">＋</span>
          <span className="mt-1 text-sm">Choose or drop images</span>
          <span className="mt-1 text-xs text-white/45">JPG, PNG or WebP · {(maxFileBytes / 1024 / 1024).toFixed(0)} MB each</span>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={event => {
            addFiles(Array.from(event.target.files ?? []));
            event.target.value = '';
          }}
        />

        {images.length > 0 && (
          <div className="mb-4 grid max-h-72 grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 md:grid-cols-4">
            {images.map((image, index) => (
              <div key={image.id} className="relative overflow-hidden rounded-lg border border-white/10 bg-black/20">
                <img src={image.previewUrl} alt={image.file.name} className="aspect-square w-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(image.id)}
                  className="absolute right-1 top-1 rounded bg-black/70 px-1.5 py-0.5 text-xs hover:bg-black"
                  aria-label={`Remove ${image.file.name}`}
                >
                  ✕
                </button>
                <div className="p-2">
                  <p className="truncate text-xs" title={image.file.name}>{index + 1}. {image.file.name}</p>
                  <p className="mt-0.5 text-[10px] text-white/45">{(image.file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {error && <p className="mb-4 text-xs text-red-300">{error}</p>}

        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-white/55">{images.length} of {maxFiles} selected</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded bg-white/10 px-4 py-2 text-sm">Cancel</button>
            <button
              type="button"
              disabled={images.length === 0}
              onClick={() => onConfirm(images.map(image => image.file))}
              className="rounded bg-[#0CB4E4] px-4 py-2 text-sm font-semibold text-black disabled:opacity-40"
            >
              Use {images.length} image{images.length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadPanel;
